import { scaleLinear } from 'd3-scale'
import { fixedDecimals } from '../../../platform/fixedDecimals'

export type Angles = { start: number; end: number }

/** A full turn, less a hair, so a lone 100% arc keeps a start before its end. */
const FULL = 2 * Math.PI - 0.0001
/**
 * Past this span the ring is drawn closed, as two half-arcs. A single SVG arc
 * whose endpoints coincide (after rounding to two decimals) is OMITTED by the
 * renderer, which would leave a lone 100% category as a blank donut.
 */
const CLOSED = 2 * Math.PI - 0.01
/** Under this span the arc's endpoints round to one point and the renderer would omit it anyway. */
const SLIVER = 0.005
const CENTRE = 50

/**
 * Where each server `sharePct` sits on the ring, clockwise from twelve o'clock.
 * The angle of a share is a linear scale from 0–100 to a full turn. The running
 * offset adds percentages, not money, and shares that stop short of 100 leave
 * the rest of the ring open rather than inventing an "Other" arc.
 */
export function arcAngles(shares: readonly number[]): Angles[] {
  const angle = scaleLinear().domain([0, 100]).range([0, 2 * Math.PI]).clamp(true)
  let offset = 0
  return shares.map((share) => {
    const start = angle(offset)
    offset += share
    return { start, end: Math.min(angle(offset), start + FULL) }
  })
}

const point = (r: number, theta: number): string =>
  `${fixedDecimals(CENTRE + r * Math.sin(theta), 2)} ${fixedDecimals(CENTRE - r * Math.cos(theta), 2)}`

/**
 * One ring segment as a closed SVG path, in a 100×100 box centred on (50, 50).
 * A sliver too thin to draw is `''`; a (near) full turn is a closed ring of two
 * half-arcs per radius, filled even-odd so the inner ring cuts the hole.
 */
export function arcPath({ start, end }: Angles, outer: number, inner: number): string {
  const span = end - start
  if (span < SLIVER) return ''
  if (span >= CLOSED) {
    const mid = start + Math.PI
    return [
      `M${point(outer, start)}`,
      `A${outer} ${outer} 0 1 1 ${point(outer, mid)}`,
      `A${outer} ${outer} 0 1 1 ${point(outer, start + 2 * Math.PI)}`,
      `M${point(inner, start)}`,
      `A${inner} ${inner} 0 1 0 ${point(inner, mid)}`,
      `A${inner} ${inner} 0 1 0 ${point(inner, start + 2 * Math.PI)}`,
      'Z',
    ].join(' ')
  }
  const large = span > Math.PI ? 1 : 0
  return [
    `M${point(outer, start)}`,
    `A${outer} ${outer} 0 ${large} 1 ${point(outer, end)}`,
    `L${point(inner, end)}`,
    `A${inner} ${inner} 0 ${large} 0 ${point(inner, start)}`,
    'Z',
  ].join(' ')
}
